import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type FulfillmentAddHandler = (() => void) | null;
export type FulfillmentBulkGeneratePalletsHandler = (() => void) | null;
export type FulfillmentBulkGenerateTotesHandler = (() => void) | null;

export interface FulfillmentTabActionsContextValue {
  onAdd: FulfillmentAddHandler;
  setOnAdd: (handler: FulfillmentAddHandler) => void;
  onBulkGeneratePallets: FulfillmentBulkGeneratePalletsHandler;
  setOnBulkGeneratePallets: (handler: FulfillmentBulkGeneratePalletsHandler) => void;
  onBulkGenerateTotes: FulfillmentBulkGenerateTotesHandler;
  setOnBulkGenerateTotes: (handler: FulfillmentBulkGenerateTotesHandler) => void;
}

const FulfillmentTabActionsContext = createContext<FulfillmentTabActionsContextValue>({
  onAdd: null,
  setOnAdd: () => {},
  onBulkGeneratePallets: null,
  setOnBulkGeneratePallets: () => {},
  onBulkGenerateTotes: null,
  setOnBulkGenerateTotes: () => {},
});

export function FulfillmentTabActionsProvider({ children }: { children: ReactNode }) {
  const [onAdd, setOnAdd] = useState<FulfillmentAddHandler>(null);
  const [onBulkGeneratePallets, setOnBulkGeneratePallets] =
    useState<FulfillmentBulkGeneratePalletsHandler>(null);
  const [onBulkGenerateTotes, setOnBulkGenerateTotes] =
    useState<FulfillmentBulkGenerateTotesHandler>(null);

  const value = useMemo(
    () => ({
      onAdd,
      setOnAdd,
      onBulkGeneratePallets,
      setOnBulkGeneratePallets,
      onBulkGenerateTotes,
      setOnBulkGenerateTotes,
    }),
    [onAdd, onBulkGeneratePallets, onBulkGenerateTotes],
  );

  return (
    <FulfillmentTabActionsContext.Provider value={value}>
      {children}
    </FulfillmentTabActionsContext.Provider>
  );
}

export function useFulfillmentTabActions() {
  return useContext(FulfillmentTabActionsContext);
}

/** Register the active fulfillment sub-tab “add” action (cleared on unmount). */
export function useFulfillmentAddHandler(handler: () => void) {
  const { setOnAdd } = useContext(FulfillmentTabActionsContext);

  useEffect(() => {
    setOnAdd(() => handler);
    return () => setOnAdd(null);
  }, [handler, setOnAdd]);
}

/** Register bulk generate pallet action for the Pallets tab (cleared on unmount). */
export function useFulfillmentBulkGeneratePalletsHandler(handler: () => void) {
  const { setOnBulkGeneratePallets } = useContext(FulfillmentTabActionsContext);

  useEffect(() => {
    setOnBulkGeneratePallets(() => handler);
    return () => setOnBulkGeneratePallets(null);
  }, [handler, setOnBulkGeneratePallets]);
}

/** Register bulk generate tote action for the Totes tab (cleared on unmount). */
export function useFulfillmentBulkGenerateTotesHandler(handler: () => void) {
  const { setOnBulkGenerateTotes } = useContext(FulfillmentTabActionsContext);

  useEffect(() => {
    setOnBulkGenerateTotes(() => handler);
    return () => setOnBulkGenerateTotes(null);
  }, [handler, setOnBulkGenerateTotes]);
}
